// Canonical engine wire emit for a WhatsApp group (#58/#61/#62).
// Both engines (chrome extension + baileys pod) project their raw
// shape — WAWeb's GroupMetadata for chrome, baileys-sdk's
// GroupMetadata for baileys — to this shape at their handler
// boundary. v1's /v1/groups mapper is a pure 1:1 projection from
// here.
//
// Field semantics:
//   name        — group display name. Chrome derives from
//                 `chat.formattedTitle || chat.name || metadata.subject`
//                 inside the handler; baileys reads `meta.subject`.
//                 Both emit as `name` — no aliases on the wire.
//   description — long-form blurb. Engines rename their internal
//                 `desc` to `description` at emit time.
//   owner       — phone-form @c.us JID, or null. Engines do the
//                 @lid→phone crossref at emit time (#61).
//   participants — array of BtSlimParticipant; owner is included in
//                 the array (`participants.length` == total member
//                 count). Each participant carries phone-form
//                 `chatId` only (#62).
//   creation    — unix-seconds group-creation timestamp.
//   announce    — group setting: admins-only post.
//   restrict    — group setting: admins-only edit info.
//
// The RAW WAWeb-store group shape and the RAW baileys group shape
// both live engine-side — bt-common consumers never see them (#74).

import { z } from 'zod';
import { WireJidSchema } from '../../contract/wire-identity';
import { BtSlimParticipantSchema } from './participant';
import { coerceLegacyGroupId } from './legacy-id';

const BtSlimGroupObjectSchema = z.object({
  groupId: WireJidSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  owner: WireJidSchema.nullable(),
  creation: z.number().optional(),
  /** Last-activity unix-seconds timestamp from the chat model (drives the
   *  public `lastMessageAt`). groupMetadata doesn't carry it; the engine
   *  reads it off the Chat model it projects from. */
  t: z.number().nullable().optional(),
  announce: z.boolean().optional(),
  restrict: z.boolean().optional(),
  /** Total member count; equals `participants.length` (owner included). */
  participantsCount: z.number(),
  participants: z.array(BtSlimParticipantSchema),
});
/** Wrapped in the legacy-id coercion so a pre-rename gateway emitting `id`
 *  (instead of `groupId`) still parses — see coerceLegacyGroupId. Nested
 *  participants are covered by BtSlimParticipantSchema's own coercion. */
export const BtSlimGroupSchema = z.preprocess(coerceLegacyGroupId, BtSlimGroupObjectSchema);
export type BtSlimGroup = z.infer<typeof BtSlimGroupSchema>;
